// src/pages/Admin/Content/SeriesManagement.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  Clock,
  Download,
  Play,
  List,
  Bell
} from 'lucide-react';
import seriesService from '../../../services/seriesService';
import adminService from '../../../services/adminService';
import type { Series, Season, Episode } from '../../../types/series';
import { useToast } from '../../../hooks/useToast';
import ConfirmDialog from '../../../components/Admin/ConfirmDialog';
import SeriesFormModal from '../../../components/Admin/SeriesFormModal';
import SeasonFormModal from '../../../components/Admin/SeasonFormModal';
import EpisodeFormModal from '../../../components/Admin/EpisodeFormModal';



interface SeriesFilters {
  seriesType: string;
  status: string;
  search: string;
  page: number;
  page_size: number;
}

const SeriesManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [series, setSeries] = useState<Series[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSeries, setSelectedSeries] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'series' | 'seasons' | 'episodes'>('series');
  const [selectedSeriesForView, setSelectedSeriesForView] = useState<Series | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);

  const [notifyingEpisodeId, setNotifyingEpisodeId] = useState<string | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => void;
    danger?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: () => {},
  });

  const [seriesFormModal, setSeriesFormModal] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit' | 'view';
    series?: Series;
  }>({
    isOpen: false,
    mode: 'add',
  });

  const [seasonFormModal, setSeasonFormModal] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit' | 'view';
    season?: Season;
    seriesId?: string;
  }>({
    isOpen: false,
    mode: 'add',
  });

  const [episodeFormModal, setEpisodeFormModal] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit' | 'view';
    episode?: Episode;
    seriesId?: string;
    seasonId?: string;
  }>({
    isOpen: false,
    mode: 'add',
  });

  // Extract series type from URL path
  const getSeriesTypeFromPath = () => {
    const path = location.pathname;
    if (path.includes('/cinema')) return 'CINEMA_SERIES';
    if (path.includes('/contents')) return 'CONTENT_SERIES';
    if (path.includes('/podcasts')) return 'PODCAST_SERIES';
    if (path.includes('/animations')) return 'ANIMATION_SERIES';
    return '';
  };

  const [filters, setFilters] = useState<SeriesFilters>({
    seriesType: getSeriesTypeFromPath(),
    status: '',
    search: '',
    page: 1,
    page_size: 20
  });

  // Update filters when URL changes
  useEffect(() => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    let seriesType = '';
    if (path.includes('/cinema')) seriesType = 'CINEMA_SERIES';
    else if (path.includes('/contents')) seriesType = 'CONTENT_SERIES';
    else if (path.includes('/podcasts')) seriesType = 'PODCAST_SERIES';
    else if (path.includes('/animations')) seriesType = 'ANIMATION_SERIES';
    
    // Override with URL params if they exist
    if (searchParams.get('seriesType')) {
      seriesType = searchParams.get('seriesType') || '';
    }
    
    const newFilters = {
      seriesType: seriesType,
      status: searchParams.get('status') || '',
      search: searchParams.get('search') || '',
      page: parseInt(searchParams.get('page') || '1'),
      page_size: parseInt(searchParams.get('page_size') || '20')
    };
    
    setFilters(prev => {
      // Check if any filter actually changed to prevent unnecessary re-renders
      const hasChanged = Object.keys(newFilters).some(key => 
        prev[key as keyof SeriesFilters] !== newFilters[key as keyof SeriesFilters]
      );
      
      if (hasChanged) {
        return newFilters;
      }
      return prev;
    });
  }, [location.pathname, location.search]);

  const seriesTypes = [
    { value: '', label: 'All Types' },
    { value: 'CINEMA_SERIES', label: 'Cinema Series' },
    { value: 'CONTENT_SERIES', label: 'Content Series' },
    { value: 'PODCAST_SERIES', label: 'Podcast Series' },
    { value: 'ANIMATION_SERIES', label: 'Animation Series' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'PUBLISHED', label: 'Published' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ARCHIVED', label: 'Archived' },
    { value: 'COMPLETED', label: 'Completed' }
  ];

  const loadSeries = useCallback(async () => {
    try {
      setSeriesLoading(true);
      // Convert component filters to service filters
      const serviceFilters = {
        page: filters.page - 1, // Backend uses 0-based indexing
        page_size: filters.page_size,
        ...(filters.seriesType && { 
          seriesType: filters.seriesType as 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES' 
        }),
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { 
          status: filters.status as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'COMPLETED' 
        })
      };
      
      const response = await seriesService.getAllSeries(serviceFilters);
      // Handle the actual API response structure
      if (response.data && response.data.content) {
        setSeries(response.data.content);
        setTotalCount(response.data.totalElements || 0);
      } else {
        setSeries([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error loading series:', error);
      setSeries([]);
      setTotalCount(0);
    } finally {
      setSeriesLoading(false);
    }
  }, [filters]);

  const loadSeasons = useCallback(async (seriesId: string) => {
    try {
      setSeasonsLoading(true);
      const data = await seriesService.getSeasonsBySeries(seriesId);
      setSeasons(data);
    } catch (error) {
      console.error('Error loading seasons:', error);
      setSeasons([]);
    } finally {
      setSeasonsLoading(false);
    }
  }, []);

  const loadEpisodes = useCallback(async (seriesId: string, seasonNumber?: number) => {
    try {
      setEpisodesLoading(true);
      let episodesData: Episode[];
      if (seasonNumber !== undefined) {
        episodesData = await seriesService.getEpisodesBySeasonNumber(seriesId, seasonNumber);
      } else {
        episodesData = await seriesService.getAllEpisodesBySeries(seriesId);
      }
      setEpisodes(episodesData || []);
    } catch (error) {
      console.error('Error loading episodes:', error);
      setEpisodes([]);
    } finally {
      setEpisodesLoading(false);
    }
  }, []);

  // Load data when view mode changes
  useEffect(() => {
    if (viewMode === 'series') {
      loadSeries();
    }
  }, [viewMode, loadSeries]);

  // Reload series when filters change
  useEffect(() => {
    if (viewMode === 'series') {
      loadSeries();
    }
  }, [filters, loadSeries, viewMode]);

  // Load seasons when viewing seasons mode
  useEffect(() => {
    if (viewMode === 'seasons' && selectedSeriesForView) {
      loadSeasons(selectedSeriesForView.id);
    }
  }, [viewMode, selectedSeriesForView, loadSeasons]);

  // Load episodes when viewing episodes mode
  useEffect(() => {
    if (viewMode === 'episodes' && selectedSeriesForView) {
      if (selectedSeason) {
        loadEpisodes(selectedSeriesForView.id, selectedSeason.seasonNumber);
      } else {
        loadEpisodes(selectedSeriesForView.id);
      }
    }
  }, [viewMode, selectedSeriesForView, selectedSeason, loadEpisodes]);

  const handleFilterChange = (key: keyof SeriesFilters, value: string | number) => {
    const newFilters = {
      ...filters,
      [key]: value,
      page: key === 'page' ? value as number : 1
    };
    
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.seriesType) params.set('seriesType', newFilters.seriesType);
    if (newFilters.status) params.set('status', newFilters.status);
    if (newFilters.page > 1) params.set('page', newFilters.page.toString());
    if (newFilters.page_size !== 10) params.set('page_size', newFilters.page_size.toString());
    
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleSearch = (value: string) => {
    handleFilterChange('search', value);
  };

  const handleSelectSeries = (seriesId: string) => {
    setSelectedSeries(prev => 
      prev.includes(seriesId) 
        ? prev.filter(id => id !== seriesId)
        : [...prev, seriesId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSeries.length === series.length) {
      setSelectedSeries([]);
    } else {
      setSelectedSeries(series.map(item => item.id));
    }
  };

  const handleDeleteSeries = (seriesId: string, title: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Series',
      message: `Are you sure you want to delete "${title}"? This will also delete all seasons and episodes. This action cannot be undone.`,
      action: async () => {
        try {
          await seriesService.deleteSeries(seriesId);
          showSuccess('Series deleted successfully');
          loadSeries();
          setSelectedSeries(prev => prev.filter(id => id !== seriesId));
        } catch (_error) {
          showError('Failed to delete series');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      danger: true
    });
  };

  const handleDeleteSeason = (seasonId: string, title: string) => {
    if (!selectedSeriesForView) return;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Season',
      message: `Are you sure you want to delete "${title}"? This will also delete all episodes in this season. This action cannot be undone.`,
      action: async () => {
        try {
          await seriesService.deleteSeason(selectedSeriesForView!.id, seasonId);
          showSuccess('Season deleted successfully');
          loadSeasons(selectedSeriesForView!.id);
        } catch (_error) {
          showError('Failed to delete season');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      danger: true
    });
  };

  const handleDeleteEpisode = (episodeId: string, title: string) => {
    if (!selectedSeriesForView || !selectedSeason) return;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Episode',
      message: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      action: async () => {
        try {
          await seriesService.deleteEpisode(selectedSeriesForView!.id, selectedSeason!.id, episodeId);
          showSuccess('Episode deleted successfully');
          loadEpisodes(selectedSeriesForView!.id, selectedSeason!.seasonNumber);
        } catch (_error) {
          showError('Failed to delete episode');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      danger: true
    });
  };

  const handleAddSeries = () => {
    setSeriesFormModal({
      isOpen: true,
      mode: 'add',
    });
  };

  const handleEditSeries = (series: Series) => {
    setSeriesFormModal({
      isOpen: true,
      mode: 'edit',
      series,
    });
  };

  const handleViewSeries = (series: Series) => {
    setSelectedSeriesForView(series);
    setViewMode('seasons');
  };

  const handleAddSeason = () => {
    if (!selectedSeriesForView) return;
    
    setSeasonFormModal({
      isOpen: true,
      mode: 'add',
      seriesId: selectedSeriesForView.id,
    });
  };

  const handleEditSeason = (season: Season) => {
    if (!selectedSeriesForView) return;
    
    setSeasonFormModal({
      isOpen: true,
      mode: 'edit',
      season,
      seriesId: selectedSeriesForView.id,
    });
  };

  const handleViewSeason = (season: Season) => {
    setSelectedSeason(season);
    setViewMode('episodes');
  };

  const handleAddEpisode = () => {
    if (!selectedSeriesForView || !selectedSeason) return;
    
    setEpisodeFormModal({
      isOpen: true,
      mode: 'add',
      seriesId: selectedSeriesForView.id,
      seasonId: selectedSeason.id,
    });
  };

  const handleEditEpisode = (episode: Episode) => {
    if (!selectedSeriesForView || !selectedSeason) return;
    
    setEpisodeFormModal({
      isOpen: true,
      mode: 'edit',
      episode,
      seriesId: selectedSeriesForView.id,
      seasonId: selectedSeason.id,
    });
  };

  const handleNotifyEpisode = (episode: Episode) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Notify Users',
      message: `Send email notifications to all users and newsletter subscribers about "${episode.title}"?`,
      action: async () => {
        setNotifyingEpisodeId(episode.id);
        try {
          await adminService.notifyContentUsers(episode.contentId);
          showSuccess(`Notification emails are being sent for: "${episode.title}"`);
        } catch (_error) {
          showError('Failed to send notifications');
        } finally {
          setNotifyingEpisodeId(null);
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      PUBLISHED: 'bg-green-100 text-green-800',
      DRAFT: 'bg-gray-100 text-gray-800',
      ARCHIVED: 'bg-red-100 text-red-800',
      COMPLETED: 'bg-blue-100 text-blue-800'
    };

    const icons = {
      PUBLISHED: CheckCircle,
      DRAFT: Clock,
      ARCHIVED: XCircle,
      COMPLETED: CheckCircle
    };

    const Icon = icons[status as keyof typeof icons] || Clock;
    const className = styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </span>
    );
  };

  const getSeriesTypeBadge = (type: string) => {
    const colors = {
      CINEMA_SERIES: 'bg-orange-100 text-orange-800',
      CONTENT_SERIES: 'bg-blue-100 text-blue-800', 
      PODCAST_SERIES: 'bg-green-100 text-green-800',
      ANIMATION_SERIES: 'bg-orange-100 text-orange-800'
    };

    const className = colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
        {type.replace('_', ' ')}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / filters.page_size);

  const renderBreadcrumb = () => (
    <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-600 mb-4 overflow-x-auto">
      <button
        onClick={() => setViewMode('series')}
        className={`whitespace-nowrap hover:text-orange-600 ${viewMode === 'series' ? 'text-orange-600 font-medium' : ''}`}
      >
        Series
      </button>
      {selectedSeriesForView && (
        <>
          <span>/</span>
          <button
            onClick={() => setViewMode('seasons')}
            className={`hover:text-orange-600 ${viewMode === 'seasons' ? 'text-orange-600 font-medium' : ''}`}
          >
            {selectedSeriesForView.title}
          </button>
        </>
      )}
      {selectedSeason && (
        <>
          <span>/</span>
          <span className="text-orange-600 font-medium">
            Season {selectedSeason.seasonNumber}: {selectedSeason.title}
          </span>
        </>
      )}
    </div>
  );

  const renderSeriesView = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {seriesLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : !series || series.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No series found matching your criteria</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedSeries.length === series.length && series.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Series
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {series.map((item) => (
                <tr
                  key={item.id}
                  className={`hover:bg-gray-50 transition-colors ${
                    selectedSeries.includes(item.id) ? 'bg-orange-50' : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedSeries.includes(item.id)}
                      onChange={() => handleSelectSeries(item.id)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 line-clamp-1">
                          {item.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          by {item.author?.name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getSeriesTypeBadge(item.seriesType)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(item.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <List className="h-4 w-4 mr-1" />
                        {item.totalSeasons} seasons
                      </div>
                      <div className="flex items-center">
                        <Play className="h-4 w-4 mr-1" />
                        {item.totalEpisodes} episodes
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleViewSeries(item)}
                        className="text-orange-600 hover:text-orange-900 p-1 rounded transition-colors"
                        title="View Seasons"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditSeries(item)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSeries(item.id, item.title)}
                        className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSeasonsView = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {seasonsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : !seasons || seasons.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No seasons found for this series</p>
          <button
            onClick={handleAddSeason}
            className="mt-4 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Add First Season
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Season
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Episodes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Release Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {seasons.map((season) => (
                <tr key={season.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Season {season.seasonNumber}: {season.title}
                      </div>
                      <div className="text-sm text-gray-500">
                        {season.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(season.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Play className="h-4 w-4 mr-1" />
                      {season.totalEpisodes} episodes
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {season.releaseDate ? new Date(season.releaseDate).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleViewSeason(season)}
                        className="text-orange-600 hover:text-orange-900 p-1 rounded transition-colors"
                        title="View Episodes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditSeason(season)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSeason(season.id, season.title)}
                        className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderEpisodesView = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {episodesLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : !episodes || episodes.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No episodes found for this season</p>
          <button
            onClick={handleAddEpisode}
            className="mt-4 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Add First Episode
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Episode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Air Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {episodes.map((episode) => (
                <tr key={episode.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        E{episode.episodeNumber}: {episode.title}
                      </div>
                      <div className="text-sm text-gray-500">
                        {episode.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(episode.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {Math.floor(episode.durationSeconds / 60)}m {episode.durationSeconds % 60}s
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Eye className="h-4 w-4 mr-1" />
                      {episode.viewCount.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {episode.airDate ? new Date(episode.airDate).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditEpisode(episode)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {episode.status === 'PUBLISHED' && episode.contentId && (
                        <button
                          onClick={() => handleNotifyEpisode(episode)}
                          disabled={notifyingEpisodeId === episode.id}
                          className="text-green-600 hover:text-green-900 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Notify Users"
                        >
                          <Bell className={`h-4 w-4 ${notifyingEpisodeId === episode.id ? 'animate-pulse' : ''}`} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteEpisode(episode.id, episode.title)}
                        className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Series Management</h1>
            <p className="mt-1 text-sm sm:text-base text-gray-600">
              Manage series, seasons, and episodes ({totalCount} total series)
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <button 
              onClick={() => showSuccess('Export functionality coming soon!')}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg flex items-center justify-center transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            {viewMode === 'series' && (
              <button 
                onClick={handleAddSeries}
                className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Add Series</span>
                <span className="sm:hidden">Add</span>
              </button>
            )}
            {viewMode === 'seasons' && (
              <button 
                onClick={handleAddSeason}
                className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Add Season</span>
                <span className="sm:hidden">Add</span>
              </button>
            )}
            {viewMode === 'episodes' && (
              <button 
                onClick={handleAddEpisode}
                className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Add Episode</span>
                <span className="sm:hidden">Add</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      {renderBreadcrumb()}

      {/* Filters - Only show for series view */}
      {viewMode === 'series' && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
            {/* Search */}
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search series..."
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Series Type Filter */}
            <div>
              <select
                value={filters.seriesType}
                onChange={(e) => handleFilterChange('seriesType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {seriesTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Page Size */}
            <div>
              <select
                value={filters.page_size}
                onChange={(e) => handleFilterChange('page_size', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
              {selectedSeries.length > 0 && (
                <button
                  onClick={() => showSuccess('Bulk actions coming soon!')}
                  className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete ({selectedSeries.length})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Views */}
      {viewMode === 'series' && renderSeriesView()}
      {viewMode === 'seasons' && renderSeasonsView()}
      {viewMode === 'episodes' && renderEpisodesView()}

      {/* Pagination - Only for series view */}
      {viewMode === 'series' && totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
              disabled={filters.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handleFilterChange('page', Math.min(totalPages, filters.page + 1))}
              disabled={filters.page === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {((filters.page - 1) * filters.page_size) + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(filters.page * filters.page_size, totalCount)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{totalCount}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                  disabled={filters.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, filters.page - 2)) + i;
                  if (pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handleFilterChange('page', pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        filters.page === pageNum
                          ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handleFilterChange('page', Math.min(totalPages, filters.page + 1))}
                  disabled={filters.page === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        danger={confirmDialog.danger}
      />

      {/* Series Form Modal */}
      <SeriesFormModal
        isOpen={seriesFormModal.isOpen}
        mode={seriesFormModal.mode}
        series={seriesFormModal.series}
        onClose={() => setSeriesFormModal({ isOpen: false, mode: 'add' })}
        onSave={(_formData: unknown) => {
          loadSeries();
          setSeriesFormModal({ isOpen: false, mode: 'add' });
        }}
      />

      {/* Season Form Modal */}
      <SeasonFormModal
        isOpen={seasonFormModal.isOpen}
        mode={seasonFormModal.mode}
        season={seasonFormModal.season}
        seriesId={seasonFormModal.seriesId}
        onClose={() => setSeasonFormModal({ isOpen: false, mode: 'add' })}
        onSave={(_formData: unknown) => {
          if (selectedSeriesForView) {
            loadSeasons(selectedSeriesForView.id);
          }
          setSeasonFormModal({ isOpen: false, mode: 'add' });
        }}
      />

      {/* Episode Form Modal */}
      <EpisodeFormModal
        isOpen={episodeFormModal.isOpen}
        mode={episodeFormModal.mode}
        episode={episodeFormModal.episode}
        seriesId={episodeFormModal.seriesId}
        seasonId={episodeFormModal.seasonId}
        onClose={() => setEpisodeFormModal({ isOpen: false, mode: 'add' })}
        onSave={(_formData: unknown) => {
          if (selectedSeriesForView && selectedSeason) {
            loadEpisodes(selectedSeriesForView.id, selectedSeason.seasonNumber);
          }
          setEpisodeFormModal({ isOpen: false, mode: 'add' });
        }}
      />
    </div>
  );
};

export default SeriesManagement;